import type { ButtonHTMLAttributes, FunctionComponent, MouseEvent } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { L12nText } from '../../text/L12nText';

import styles from './Button.module.css';

export enum ButtonVariant {
  MAIN = 'main',
  ACCENT = 'accent',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tk?: string;
  badge?: string | number;
  variant: ButtonVariant;
  loading?: boolean;
  disabled?: boolean;
}

export const Button: FunctionComponent<ButtonProps> = ({
  tk,
  variant,
  badge,
  children,
  loading = false,
  disabled = false,
  className,
  onClick,
  ...rest
}) => {
  const audioTrigger = useAudioTrigger();

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    audioTrigger.play({ name: 'menuClick' });
    onClick?.(event);
  };

  return (
    <button
      data-testid="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={handleClick}
      className={`${styles.button} ${styles['button__' + variant]} ${className || ''}`}
      {...rest}
    >
      {loading ? (
        <svg className={styles.loading} viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="11" strokeWidth="2" strokeDasharray="28" strokeLinecap="round" />
        </svg>
      ) : null}
      <span data-testid="button-content" className={styles.content}>
        {tk ? <L12nText tk={tk} /> : children}
      </span>
      {badge ? (
        <span data-testid="badge" className={styles.badge}>
          {badge}
        </span>
      ) : null}
    </button>
  );
};
